/**
 * 动态文化配置工具
 * 支持从 JSON 配置动态创建格式化函数，不依赖硬编码的 CULTURE_CONFIGS
 */

import type { CultureConfig } from "@/api/country";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 启用 dayjs 时区插件
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 通用函数：提取嵌套的属性
 */
export function extractNestedProperty(data: any, propertyName: string): any {
  if (data && typeof data === 'object' && propertyName in data) {
    return extractNestedProperty(data[propertyName], propertyName);
  } else if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return extractNestedProperty(parsed, propertyName);
    } catch (error) {
      return data;
    }
  } else {
    return data;
  }
}

/**
 * 递归解析JSON，处理嵌套的JSON字符串
 */
export function recursiveJsonParse(data: any): any {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return recursiveJsonParse(parsed);
    } catch (error) {
      return data;
    }
  } else if (Array.isArray(data)) {
    return data.map(item => recursiveJsonParse(item));
  } else if (data !== null && typeof data === 'object') {
    const result: any = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = recursiveJsonParse(data[key]);
      }
    }
    return result;
  } else {
    return data;
  }
}

/**
 * 处理日期格式数据，处理嵌套的date_format结构
 */
export function processDateFormat(data: any): any {
  const extracted = extractNestedProperty(data, 'date_format');
  return typeof extracted === 'object' && extracted !== null ? extracted : {};
}

/**
 * 构建日期格式的display字段
 * @param dateFormat 日期格式（如 YYYYMMDD 或 YYYY-MM-DD）
 * @param separator 分隔符
 * @param timeFormat 时间格式
 * @returns display字符串
 */
export function buildDateFormatDisplay(dateFormat: string, separator: string, timeFormat: string): string {
  let display = "";
  if (!dateFormat) return display;
  
  // 检查日期格式是否包含分隔符
  const hasSeparator = /[^YMD]/.test(dateFormat);
  let formattedDate = dateFormat;
  
  if (!hasSeparator && separator && dateFormat.length === 8) {
    // 没有分隔符且日期格式为8位，需要添加分隔符
    formattedDate = dateFormat.substring(0, 4) + separator + 
                   dateFormat.substring(4, 6) + separator + 
                   dateFormat.substring(6, 8);
  }
  
  display = formattedDate;
  
  // 添加时间格式
  if (timeFormat) {
    const formattedTime = timeFormat.replace(/[^0-9a-zA-Z]/g, ":");
    display += " " + formattedTime;
  }
  
  return display;
}

/**
 * 处理文本样式数据，处理嵌套的text_style结构
 */
export function processStyleForm(data: any): any {
  const extracted = extractNestedProperty(data, 'text_style');
  return typeof extracted === 'object' && extracted !== null ? extracted : {};
}

/**
 * 解析文化配置（支持字符串和对象格式）
 */
export function parseCultureConfig(
  config: CultureConfig | string | undefined,
): CultureConfig | null {
  if (!config) return null;

  if (typeof config === "string") {
    try {
      return JSON.parse(config) as CultureConfig;
    } catch (error) {
      console.error("解析文化配置失败:", error);
      return null;
    }
  }

  return config;
}

/**
 * 格式化日期（支持时区转换）
 */
export function formatDate(
  date: Date | string,
  config: CultureConfig,
  timezoneCode?: string,
): string {
  let dateObj: dayjs.Dayjs;

  if (typeof date === "string") {
    dateObj = dayjs(date);
  } else {
    dateObj = dayjs(date);
  }

  if (!dateObj.isValid()) {
    return "";
  }

  // 如果指定了时区，转换为该时区
  if (timezoneCode) {
    dateObj = dateObj.tz(timezoneCode);
  }

  const { display, separator } = config.dateFormat;

  // 根据显示格式格式化日期
  switch (display) {
    case "YYYY-MM-DD":
      return dateObj.format("YYYY-MM-DD").replace(/-/g, separator);
    case "MM/DD/YYYY":
      return dateObj.format("MM/DD/YYYY").replace(/\//g, separator);
    case "DD/MM/YYYY":
      return dateObj.format("DD/MM/YYYY").replace(/\//g, separator);
    case "DD.MM.YYYY":
      return dateObj.format("DD.MM.YYYY").replace(/\./g, separator);
    case "YYYY年MM月DD日":
      return dateObj.format("YYYY年MM月DD日");
    case "YYYY.MM.DD":
      return dateObj.format("YYYY.MM.DD").replace(/\./g, separator);
    default:
      // 自定义格式：使用 dayjs 格式化
      return dateObj.format(display);
  }
}

/**
 * 格式化时间（支持时区转换）
 */
export function formatTime(
  date: Date | string,
  config: CultureConfig,
  timezoneCode?: string,
): string {
  let dateObj: dayjs.Dayjs;

  if (typeof date === "string") {
    dateObj = dayjs(date);
  } else {
    dateObj = dayjs(date);
  }

  if (!dateObj.isValid()) {
    return "";
  }

  // 如果指定了时区，转换为该时区
  if (timezoneCode) {
    dateObj = dateObj.tz(timezoneCode);
  }

  const { display, is24Hour } = config.timeFormat;

  if (is24Hour) {
    return dateObj.format(display || "HH:mm:ss");
  } else {
    return dateObj.format(display || "h:mm:ss A");
  }
}

/**
 * 格式化日期时间（支持时区转换）
 */
export function formatDateTime(
  date: Date | string,
  config: CultureConfig,
  timezoneCode?: string,
): string {
  const dateStr = formatDate(date, config, timezoneCode);
  const timeStr = formatTime(date, config, timezoneCode);
  return `${dateStr} ${timeStr}`;
}

/**
 * 格式化数字
 */
export function formatNumber(value: number, config: CultureConfig): string {
  const { decimalSeparator, thousandsSeparator, decimalPlaces } =
    config.numberFormat;

  // 固定小数位数
  const fixed = value.toFixed(decimalPlaces);
  const [integer, decimal] = fixed.split(".");

  // 添加千分位分隔符
  const formattedInteger = integer.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousandsSeparator,
  );

  if (decimal && decimalPlaces > 0) {
    return `${formattedInteger}${decimalSeparator}${decimal}`;
  }

  return formattedInteger;
}

/**
 * 格式化货币
 */
export function formatCurrency(value: number, config: CultureConfig): string {
  const formattedNumber = formatNumber(value, config);
  const { symbol, position } = config.currency;

  return position === "before"
    ? `${symbol}${formattedNumber}`
    : `${formattedNumber}${symbol}`;
}

/**
 * 解析数字字符串（根据配置的分隔符）
 */
export function parseNumber(
  value: string,
  config: CultureConfig,
): number | null {
  if (!value) return null;

  const { decimalSeparator, thousandsSeparator } = config.numberFormat;

  // 移除千分位分隔符
  let cleaned = value.replace(new RegExp(`\\${thousandsSeparator}`, "g"), "");

  // 将小数分隔符替换为点号
  if (decimalSeparator !== ".") {
    cleaned = cleaned.replace(new RegExp(`\\${decimalSeparator}`, "g"), ".");
  }

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
}

/**
 * 验证日期格式
 */
export function validateDate(
  dateString: string,
  config: CultureConfig,
): boolean {
  if (!dateString) return false;

  const { display, separator } = config.dateFormat;

  // 根据格式验证
  switch (display) {
    case "YYYY-MM-DD":
    case "MM/DD/YYYY":
    case "DD/MM/YYYY":
    case "DD.MM.YYYY":
    case "YYYY.MM.DD":
      const pattern = display
        .replace("YYYY", "\\d{4}")
        .replace("MM", "\\d{2}")
        .replace("DD", "\\d{2}")
        .replace(/-/g, `\\${separator}`)
        .replace(/\//g, `\\${separator}`)
        .replace(/\./g, `\\${separator}`);
      return new RegExp(`^${pattern}$`).test(dateString);
    default:
      return true; // 自定义格式暂不验证
  }
}

/**
 * 创建格式化器函数（用于表格列等场景）
 */
export function createFormatters(config: CultureConfig | null) {
  if (!config) {
    // 返回默认格式化器
    return {
      date: (date: Date | string) => {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleDateString();
      },
      time: (date: Date | string) => {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleTimeString();
      },
      dateTime: (date: Date | string) => {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleString();
      },
      number: (value: number) => value.toLocaleString(),
      currency: (value: number) => `$${value.toLocaleString()}`,
    };
  }

  return {
    date: (date: Date | string) => formatDate(date, config),
    time: (date: Date | string) => formatTime(date, config),
    dateTime: (date: Date | string) => formatDateTime(date, config),
    number: (value: number) => formatNumber(value, config),
    currency: (value: number) => formatCurrency(value, config),
  };
}
